import {useLayoutEffect} from 'react'

const PHONE_QUERY = '(max-width: 640px)'
// Room above the prints for their remove buttons, which sit on the corners.
const ROOM_FOR_REMOVE_PX = {phone: 22, wide: 28}
// The top-row buttons keep this much space either side of the label.
const SIDE_CLEARANCE_PX = 16

/**
 * While a story shows photos, moves the chapter label up into the empty space
 * at the top of the screen so the prints take that space rather than pushing
 * the question and writing box down (TK-3108, AC 2.2): into the top row
 * between "Save and close" and "Feeling stuck?" when it fits there, otherwise
 * just below them. Re-placed on resize; put back when the photos go.
 */
const useAnchoredChapterLabel = ({active, labelRef, topRef}) => {
  useLayoutEffect(() => {
    const label = labelRef.current

    if (!label) return

    const reset = () => {
      label.style.marginTop = ''
      label.style.marginBottom = ''
    }

    reset()

    if (!active) return

    const place = () => {
      reset()

      const rect = label.getBoundingClientRect()
      const naturalTop = rect.top + window.scrollY
      const buttons = topRef.current ? Array.from(topRef.current.querySelectorAll('button')) : []
      let target = naturalTop

      if (buttons.length > 0) {
        const center = rect.left + (rect.width / 2)
        const halfWidth = (rect.width / 2) + SIDE_CLEARANCE_PX
        let fits = true
        let middle = -Infinity
        let bottom = -Infinity

        buttons.forEach(button => {
          const buttonRect = button.getBoundingClientRect()

          middle = Math.max(middle, buttonRect.top + (buttonRect.height / 2) + window.scrollY)
          bottom = Math.max(bottom, buttonRect.bottom + window.scrollY)

          if (buttonRect.right > center - halfWidth && buttonRect.left < center + halfWidth) fits = false
        })

        target = Math.min(naturalTop, fits ? middle - (rect.height / 2) : bottom + 6)
      }

      const marginTop = parseFloat(window.getComputedStyle(label).marginTop) || 0
      const phone = window.matchMedia && window.matchMedia(PHONE_QUERY).matches

      label.style.marginTop = `${Math.round(marginTop - (naturalTop - target))}px`
      label.style.marginBottom = `${phone ? ROOM_FOR_REMOVE_PX.phone : ROOM_FOR_REMOVE_PX.wide}px`
    }

    place()
    window.addEventListener('resize', place)

    return () => {
      window.removeEventListener('resize', place)
      reset()
    }
  }, [active, labelRef, topRef])
}

export default useAnchoredChapterLabel
